import { exportBundleSchema } from '@datum/shared'
import { useQueryClient } from '@tanstack/react-query'
import { Download, TriangleAlert, X } from 'lucide-react'
import { useState } from 'react'
import { api } from '../lib/api'
import { signIn } from '../lib/auth'
import { clearAllLocal } from '../lib/offline'

interface DataPanelProps {
  onClose: () => void
  /** Email of the signed-in user — the confirm password doubles as a re-auth. */
  email: string
}

/**
 * Account data lane: full JSON export (with fresh signed media links) and the
 * irreversible account deletion, gated behind word + password confirmation.
 */
export function DataPanel({ onClose, email }: DataPanelProps) {
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState<'export' | 'delete' | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirmWord, setConfirmWord] = useState('')
  const [password, setPassword] = useState('')

  async function downloadExport(): Promise<void> {
    if (busy) return
    setBusy('export')
    setError(null)
    setNotice(null)
    try {
      const response = await api.api.export.me.$get()
      if (!response.ok) {
        setError(`export failed (${response.status})`)
        return
      }
      const parsed = exportBundleSchema.safeParse(await response.json())
      if (!parsed.success) {
        setError('export payload did not match the contract')
        return
      }
      const blob = new Blob([JSON.stringify(parsed.data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `datum-export-${new Date().toISOString().slice(0, 10)}.json`
      anchor.click()
      URL.revokeObjectURL(url)
      setNotice(`exported ${parsed.data.points.length} point(s), ${parsed.data.attachments.length} attachment(s)`)
    } catch {
      setError('export request failed — check connection')
    } finally {
      setBusy(null)
    }
  }

  async function deleteAccount(): Promise<void> {
    if (busy || confirmWord !== 'DELETE' || password.length === 0) return
    if (!window.confirm('This permanently deletes your account, notes, organizations and media. Export first if unsure. Continue?')) return
    setBusy('delete')
    setError(null)
    setNotice(null)
    try {
      // The confirmation password first re-authenticates: better-auth gates
      // verify-password on session freshness, so a long-lived session must be
      // re-signed-in for the server-side check to run at all.
      const result = await signIn(email, password)
      if (!result.signedIn) {
        setError('password confirmation failed')
        return
      }
      const response = await api.api.account.me.$delete({ json: { password } })
      if (!response.ok) {
        setError(response.status === 403 ? 'password confirmation failed' : `deletion failed (${response.status})`)
        return
      }
      const data = (await response.json()) as { organizations_removed: number; media_objects_removed: number }
      await clearAllLocal()
      queryClient.clear()
      setNotice(`account deleted — ${data.organizations_removed} org(s), ${data.media_objects_removed} media object(s) removed`)
      // queryClient.clear() drops the session cache; App re-renders to AuthGate.
    } catch {
      setError('deletion request failed — check connection')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-label="Data and account"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl border border-edge-strong bg-ink-900 p-5"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">Data &amp; account</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-zinc-500 hover:text-zinc-200">
            <X size={15} />
          </button>
        </div>

        <section className="flex flex-col gap-2">
          <p className="text-[13px] text-zinc-300">
            Export your complete dataset — profile, organizations, waypoints, every note (including
            tombstones) and attachment metadata with signed download links (valid 15 minutes).
          </p>
          <button
            type="button"
            onClick={() => void downloadExport()}
            disabled={busy !== null}
            className="mono flex items-center justify-center gap-2 rounded border border-edge bg-ink-800 px-3 py-2 text-[11px] uppercase tracking-[0.08em] text-zinc-200 hover:border-edge-strong disabled:opacity-50"
          >
            <Download size={13} />
            {busy === 'export' ? 'exporting…' : 'download export (.json)'}
          </button>
        </section>

        <section className="mt-5 rounded border border-conflict/30 bg-conflict/5 p-3">
          <div className="mono mb-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.08em] text-conflict">
            <TriangleAlert size={13} />
            danger zone
          </div>
          <p className="mb-3 text-[12px] leading-relaxed text-zinc-400">
            Deletes your account, your notes, sole-member organizations and their stored media.
            Notes you authored inside shared organizations are also removed — export first.
          </p>
          <div className="flex flex-col gap-2">
            <input
              value={confirmWord}
              onChange={(event) => setConfirmWord(event.target.value)}
              placeholder='type DELETE'
              aria-label="Confirmation word"
              className="mono rounded border border-edge bg-ink-850 px-3 py-2 text-[12px] text-zinc-100 outline-none placeholder:text-zinc-600 focus:border-edge-strong"
            />
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="account password"
              aria-label="Account password"
              autoComplete="current-password"
              className="rounded border border-edge bg-ink-850 px-3 py-2 text-[12px] text-zinc-100 outline-none placeholder:text-zinc-600 focus:border-edge-strong"
            />
            <button
              type="button"
              onClick={() => void deleteAccount()}
              disabled={busy !== null || confirmWord !== 'DELETE' || password.length === 0}
              className="mono rounded border border-conflict/40 bg-conflict/10 px-3 py-2 text-[11px] uppercase tracking-[0.08em] text-conflict hover:bg-conflict/20 disabled:opacity-40"
            >
              {busy === 'delete' ? 'deleting…' : 'delete my account'}
            </button>
          </div>
        </section>

        {notice !== null && (
          <p className="mono mt-3 rounded border border-sync/30 bg-sync/10 px-2 py-1.5 text-[11px] text-sync">{notice}</p>
        )}
        {error !== null && (
          <p className="mono mt-3 rounded border border-conflict/30 bg-conflict/10 px-2 py-1.5 text-[11px] text-conflict">{error}</p>
        )}
      </div>
    </div>
  )
}
